<template>
  <div class="wrp p-4" v-loading="isLoading" v-if="hasAnapath">
    <div class="flex justify-between items-center mb-2">
      <h2>
        <span>{{ $t("side_menu.lbl_pathology") }}</span>
        <span v-show="reloading" class="ml-2 text-xs bg-green-300 text-green-800 italic transition-all duration-300">{{ $t("lbl_loading") }}...</span>
      </h2>
      <div>
        <ButtonHoDo @click="showModalAddPatient(true)" :title="$t('button.add_new_case')" class-name="bg-blue-700 text-white rounded-lg h-9 px-3 py-2" />
      </div>
    </div>
    <div class="rounded-xl bg-white p-3">
      <FiltersHeader :filters="filtersData" @setFilters="setFiltersData" />

      <div v-if="listPathologies">
        <el-table ref="listPathologiesTable" :data="listPathologies" class="w-full" row-class-name="cursor-pointer" header-cell-class-name="text-black" @row-click="handleViewDetail">
          <el-table-column property="pid" :label="$t('lbl_patient_code')" show-overflow-tooltip></el-table-column>
          <el-table-column property="created_at" :label="$t('title.test_date')" show-overflow-tooltip>
            <template slot-scope="scope">
              {{ appUtils.formatDateTime(scope.row.created_at) }}
            </template>
          </el-table-column>
          <el-table-column property="diagnose_by_user.name" :label="$t('title.attending_doctor')" show-overflow-tooltip></el-table-column>
          <el-table-column property="status" :label="$t('state.state')">
            <template slot-scope="scope">
              <div :set="[(s = getStatus(scope.row.status))]">
                <i :class="`${s.color} ${s.icon}`" />
                <span class="ml-2" :class="s.color">{{ s.title }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column property="analysis_diagnose" :label="$t('title.doctor_conclusion')" show-overflow-tooltip></el-table-column>
          <el-table-column property="diagnose" :label="$t('lbl_description')" show-overflow-tooltip></el-table-column>
        </el-table>

        <div v-if="pagination && pagination.total > 1">
          <div class="flex mt-3 flex-col md:flex-row justify-between items-center gap-2">
            <div class="fs-16">{{ $t("lbl_pagination", { to: countItems, total: pagination.total }) }}</div>

            <el-pagination background :current-page.sync="page" :pager-count="5" :page-size="pagination.page_size" layout="slot, prev, pager, next" :total="pagination.total" @current-change="handleChangePage" @next-click="handleChangePage" @prev-click="handleChangePage">
              <el-select v-model="selectedPageSize" default-first-option size="mini" :placeholder="$t('lbl_num_per_page')" @change="handleSizeChange">
                <el-option v-for="item in pageSizes" :key="item.label" :label="item.label" :value="item.value"> </el-option>
              </el-select>
            </el-pagination>
          </div>
        </div>
      </div>
    </div>

    <ModalFindPatient ref="ModalFindPatient" />
  </div>
</template>

<script>
import { ButtonHoDo } from '@/components/Button'
import appUtils from '@/utils/appUtils'
import { mixinHasFeatures } from '@/utils/mixinHasFeatures'
import { debounce } from 'lodash'
import { mapActions, mapState } from 'vuex'
import FiltersHeader from './Common/FiltersHeader'
import ModalFindPatient from './Common/ModalFindPatient'

export default {
  name: 'Anapath',
  components: { ButtonHoDo, ModalFindPatient, FiltersHeader },
  mixins: [mixinHasFeatures],
  data () {
    const pageSizesInitial = [
      {
        label: this.$t('title.lbl_number_of_page', { number: 10 }),
        value: 10
      },
      {
        label: this.$t('title.lbl_number_of_page', { number: 20 }),
        value: 20
      },
      {
        label: this.$t('title.lbl_number_of_page', { number: 50 }),
        value: 50
      },
      {
        label: this.$t('title.lbl_number_of_page', { number: 100 }),
        value: 100
      },
      {
        label: this.$t('title.lbl_number_of_page', { number: 200 }),
        value: 200
      },
      {
        label: this.$t('title.lbl_number_of_page', { number: 500 }),
        value: 500
      },
      {
        label: this.$t('lbl_all'),
        value: -1
      }
    ]

    const initParams = {
      sort_by: 'id',
      order: 'desc',
      page_num: 1,
      page_size: pageSizesInitial[0].value,
      ws_id: this.$clinicId
    }

    return {
      isLoading: true,
      appUtils,
      listPathologies: null,
      pagination: null,
      page: 1,
      pageSizesInitial,
      pageSizes: pageSizesInitial,
      selectedPageSize: pageSizesInitial[0].value,
      filtersData: null,
      initParams,
      paramsData: initParams,
      timer: null,
      reloading: false,
      toRoute: null,
      fromRoute: null
    }
  },
  computed: {
    ...mapState({
      prevRouter: state => state.app.prevRouter
    }),
    countItems () {
      return this.page * this.pagination.page_size > this.pagination.total ? this.pagination.total : this.page * this.pagination.page_size
    }
  },
  watch: {
    paramsData: {
      async handler () {
        await this.handleGetListPathology()
      },
      deep: true
    }
  },
  async created () {
    const self = this

    if (self.$route.name === 'AnapathPage' || self.$route.name === 'DetailPathology') {
      self.handleChangeRoute(self.prevRouter || null)
    }

    await self.handleGetListPathology()

    self.timer = setInterval(async () => {
      self.reloading = true
      await self.handleGetListPathology().then(() => {
        self.reloading = false
      })
    }, 1000 * 10) // refetch 30s
  },
  async mounted () {
    setTimeout(() => {
      this.isLoading = false
    }, 750)
  },
  methods: {
    ...mapActions('app', ['savePrevRouter']),
    handleChangeRoute (data) {
      if (data !== null && data?.query) {
        const { page, page_size, status, from_date, to_date, s } = data?.query
        const filterData = {
          ...this.paramsData,
          page_num: page,
          page_size,
          status: status === 'all' || status === '' ? '' : status === 'processing' ? 1 : status === 'results' ? 2 : 1,
          from_date: from_date ? window.moment(from_date, 'DD-MM-YYYY').valueOf() : null,
          to_date: to_date ? window.moment(to_date, 'DD-MM-YYYY').valueOf() : null,
          keyword: s
        }
        this.setFiltersData(filterData, false)
        if (Number(this.selectedPageSize) !== Number(page_size)) {
          this.handleSizeChange(page_size, false)
        }
        this.handleChangePage(page, true)
        this.handleChangeRoute()
        return
      }
      const { page_num, page_size, status, from_date, to_date, keyword } = this.paramsData
      const query = {
        page: page_num,
        page_size,
        status: status === -1 || status === 'all' || status === '' ? 'all' : status === 1 ? 'processing' : status === 2 ? 'results' : '',
        from_date: from_date ? window.moment(from_date).format('DD-MM-YYYY') : '',
        to_date: to_date ? window.moment(to_date).format('DD-MM-YYYY') : '',
        s: keyword || ''
      }
      this.$router.push({ query }).catch(() => {})
      this.savePrevRouter(this.$route)
    },
    cancelAutoUpdate () {
      clearInterval(this.timer)
    },
    showModalAddPatient (value) {
      this.$refs.ModalFindPatient.handleShowModal(value, 'anapath')
    },
    setFiltersData (data, reload = true) {
      if (!data) return

      this.filtersData = data
      this.paramsData = {
        ...this.paramsData,
        ...data
      }
      if (reload) this.handleChangeRoute()
    },
    getStatus (value) {
      switch (value) {
        case 1:
          return {
            color: 'text-yellow-500',
            icon: 'el-icon-loading',
            title: this.$t('title.lbl_processing')
          }
        case 2:
          return {
            color: 'text-green-500',
            icon: 'el-icon-success',
            title: this.$t('title.lbl_has_result')
          }
        default:
          return {
            color: 'text-red-500',
            icon: 'el-icon-error',
            title: this.$t('title.lbl_unknown')
          }
      }
    },
    async handleGetListPathology () {
      try {
        this.isLoading = true

        const res = await this.$rf.getRequest('RisRequest').getListPathology(this.paramsData)
        if (!res.data) return

        this.listPathologies = res.data?.data
        this.pagination = res.data?.page
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
    handleChangePage: debounce(function (newPage, reload = true) {
      this.page = Number(newPage)
      this.paramsData = {
        ...this.paramsData,
        page_num: Number(newPage)
      }
      if (reload) this.handleChangeRoute()
    }, 350),
    handleSizeChange: debounce(function (value, reload = true) {
      this.selectedPageSize = Number(value)
      this.paramsData = {
        ...this.paramsData,
        page_size: Number(value) === -1 ? this.pagination.total : Number(value)
      }
      this.handleChangePage(1, false)
      if (reload) this.handleChangeRoute()
    }, 350),
    handleViewDetail (record) {
      if (!record?.id) return

      this.$router
        .push({
          name: 'DetailPathology',
          params: {
            id: record.id
          }
        })
        .catch(() => {})
    }
  },
  beforeRouteLeave (to, from, next) {
    this.toRoute = to
    this.fromRoute = from
    if (this.toRoute?.name !== 'AnapathPage' && this.toRoute?.name !== 'DetailPathology') {
      this.savePrevRouter(null)
    }
    next()
  },
  beforeDestroy () {
    this.$router
      .push({
        query: {}
      })
      .catch(() => {})
  },
  destroyed () {
    this.cancelAutoUpdate()
  }
}
</script>
